import React from "react";
import {string, array} from "yup";
import {Button, FormGroup, Stack, TextInput, Text} from "@unibuddy/patron";
import {DateInput} from "ubcommunity-shared/src/Patron/DateInput/DateInput";

import {AutocompleteV2} from "ubcommunity-shared/src/Patron/AutocompleteV2";
import type {
    BaseCustomAttributeField,
    SingleChoiceAttributeField,
} from "ubcommunity-shared/src/types";
import {CustomAttributeType} from "ubcommunity-shared/src/SignUpStepTwo/useDiscoverableUniversity";

export type AllCustomAttributeFields = Array<
    BaseCustomAttributeField | SingleChoiceAttributeField | null
>;
type CustomAttributesSectionProps = {
    attributes: AllCustomAttributeFields;
    initialValues?: Record<string, any>;
    handleFormChange?: (value: any) => void;
};

const SUPPORTED_CUSTOM_ATTRIBUTES = [
    CustomAttributeType.SINGLE_SELECT,
    CustomAttributeType.TEXT_FIELD,
    CustomAttributeType.DATE,
];

export const CustomAttributesSection: React.FC<CustomAttributesSectionProps> = ({
    attributes,
    initialValues = {},
    handleFormChange = () => {},
}) => {
    const handleOnDropdownOpened = () => {};

    return (
        <Stack space="large">
            {attributes
                .filter(
                    (attribute) =>
                        attribute &&
                        // Filter out unsupported custom attributes and static* attributes
                        SUPPORTED_CUSTOM_ATTRIBUTES.includes(attribute?.type) &&
                        !attribute?.type?.includes("static"),
                )
                .map((attribute) => {
                    const {id, type, name, options, required} = attribute;

                    let validation;
                    if (type === CustomAttributeType.SINGLE_SELECT) {
                        validation = array().nullable();
                        if (required) {
                            validation = validation
                                .required("This field is required")
                                .min(1, "This field is required");
                        }
                    } else {
                        validation = string().nullable();
                        if (required) {
                            validation = validation.required("This field is required");
                        }
                    }

                    return (
                        <FormGroup
                            key={id}
                            label={name}
                            id={id}
                            initialValue={initialValues[id] ?? null}
                            validation={validation}
                            onChange={(e) => {
                                handleFormChange(e);
                            }}
                        >
                            {(() => {
                                switch (type) {
                                    case CustomAttributeType.SINGLE_SELECT:
                                        return (
                                            <AutocompleteV2
                                                id={id}
                                                name={id}
                                                data={options ?? []}
                                                placeholder="Select"
                                                renderItem={(item) => (
                                                    <Text size="medium">{item?.value}</Text>
                                                )}
                                                resultKey="value"
                                                onDropdownOpened={handleOnDropdownOpened}
                                                required={required}
                                            />
                                        );
                                    case CustomAttributeType.DATE:
                                        return (
                                            <DateInput
                                                id={id}
                                                name={id}
                                                showMonthDropdown
                                                showYearDropdown
                                                required={required}
                                                placeholder="Select Date"
                                                todayButton={
                                                    <Button size="small" clear>
                                                        <Text>Today</Text>
                                                    </Button>
                                                }
                                            />
                                        );
                                    case CustomAttributeType.TEXT_FIELD:
                                        return <TextInput id={id} name={id} required={required} />;
                                    default:
                                        return null;
                                }
                            })()}
                        </FormGroup>
                    );
                })}
        </Stack>
    );
};
